import { Entity } from "@common/types/entity";
import { formatISO } from "date-fns";
import { exportBalanceSheet } from "@api/accountingReports.api";
import { AccountingReportFilters } from "@components/accounting/types";
import {
    getReportEntityTitle,
    getReportTypeTitle,
    ReportFormat,
} from "@common/helpers/reports";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_LABELS,
} from "@common/types/reports/report-type";
import { saveBlobResponse, useFileDownload } from "@lib/fileDownload";
import { ReportSettingRowVisibility } from "@common/types/reports/report-settings";
import { pickXlsxExportRowVisibility } from "@components/accounting/xlsxExportRowVisibility";

interface DownloadParameters {
    entity: Entity;
    format: ReportFormat;
    date?: Date;
}

export function useBalanceSheetDownload(
    requestDto: AccountingReportFilters,
    rowVisibility?: ReportSettingRowVisibility,
) {
    return useFileDownload(
        async ({ entity, format, date }: DownloadParameters) => {
            if (!requestDto) {
                return;
            }

            const modifiedRequestDto = {
                ...requestDto,
                format,
            };

            const exportRowVisibility = pickXlsxExportRowVisibility(
                format,
                rowVisibility,
            );

            const response = await exportBalanceSheet(
                modifiedRequestDto,
                exportRowVisibility,
            );

            if (!response) {
                return;
            }

            const entityTitle = getReportEntityTitle(
                entity.name ?? "",
                requestDto.cycle,
            );
            const reportTypeTitle = getReportTypeTitle(
                ACCOUNTING_REPORT_LABELS[AccountingReportType.BALANCE_SHEET],
                requestDto.cycle,
            );
            const fileName = `${entityTitle} - ${reportTypeTitle} - ${date ? formatISO(date, { representation: "date" }) : ""}.${format}`;

            saveBlobResponse(response, fileName);
        },
    );
}
